/**
 * Centralized Onboarding & Feature Announcement Service
 * Handles showing tooltips/announcements for new features
 * Frontend-only implementation using localStorage
 */

class OnboardingService {
  constructor() {
    this.userSeenFeatures = new Set();
    this.pendingAnnouncements = [];
    this.isInitialized = false;
  }

  /**
   * Initialize with user's seen features (localStorage-based for now)
   */
  initialize(userId) {
    // Don't re-initialize if already initialized for this user
    if (this.isInitialized && this.userId === userId) {
      return;
    }

    // Use provided userId or fallback to 'guest' for development
    const effectiveUserId = userId || "guest";

    // Load user's seen features from localStorage
    const storageKey = `userSeenFeatures_${effectiveUserId}`;
    const localSeen = JSON.parse(localStorage.getItem(storageKey) || "[]");

    this.userSeenFeatures = new Set(localSeen);
    this.userId = effectiveUserId;
    this.isInitialized = true;

    // Process any pending announcements that were queued before init
    this.processPendingAnnouncements();
  }

  /**
   * Check if user should see a feature announcement
   */
  shouldShowFeature(featureId, conditions = {}) {
    if (!this.isInitialized) {
      // Queue for later processing
      this.pendingAnnouncements.push({ featureId, conditions });
      return false;
    }

    // Already seen this feature
    if (this.userSeenFeatures.has(featureId)) {
      return false;
    }

    // Check additional conditions
    if (conditions.requireMultipleLocales) {
      if (
        !conditions.supportedLocales ||
        conditions.supportedLocales.length <= 1
      ) {
        return false;
      }
    }

    if (
      conditions.userRole &&
      !conditions.allowedRoles?.includes(conditions.userRole)
    ) {
      return false;
    }

    return true;
  }

  /**
   * Mark a feature as seen by the user
   */
  markFeatureAsSeen(featureId) {
    this.userSeenFeatures.add(featureId);

    if (this.userId) {
      // Save to localStorage
      const storageKey = `userSeenFeatures_${this.userId}`;
      const localSeen = Array.from(this.userSeenFeatures);
      localStorage.setItem(storageKey, JSON.stringify(localSeen));
    }
  }

  /**
   * Process announcements that were queued before initialization
   */
  processPendingAnnouncements() {
    this.pendingAnnouncements.forEach(({ featureId, conditions, callback }) => {
      if (callback && this.shouldShowFeature(featureId, conditions)) {
        callback();
      }
    });
    this.pendingAnnouncements = [];
  }

  /**
   * Register a feature announcement with callback
   */
  registerFeatureAnnouncement(featureId, conditions, showCallback) {
    if (!this.isInitialized) {
      this.pendingAnnouncements.push({
        featureId,
        conditions,
        callback: showCallback,
      });
      return;
    }

    if (this.shouldShowFeature(featureId, conditions)) {
      showCallback();
    }
  }

  /**
   * Get all unseen features for debugging
   */
  getUnseenFeatures(availableFeatures) {
    return availableFeatures.filter(
      (featureId) => !this.userSeenFeatures.has(featureId)
    );
  }
}

// Export singleton instance
export default new OnboardingService();
